import { type FormEvent, type ReactElement, useCallback, useEffect, useState } from 'react';
import {
  type ApiError, type PartnerBillingAccount, type PartnerStatement, type PartnerStatementDetail, type PartnerStatementLine, type PartnerStatementPayment,
} from '@vello/api-client';
import { Badge, Button, Drawer, EmptyState, Field, ListTable, MoneyInput, Notice, PageHeader, Panel, Select, TextInput, type Column, SectionTitle, DrawerHeader, DrawerBody, Cluster, Stack, Text } from '@vello/web-theme';
import {
  maySelfApprove,
  CREDIT_NOTE_REASON_CODES, EARLY_CLOSE_REASON_CODES, PAYMENT_REJECT_REASON_CODES, canDecidePayment, canIssueCreditNote, canRecordPayment, exposurePermille,
  formatMarketInstant, formatWireMoney, partnerPaymentTone, partnerStatementTone, refundAuthority, statementAmountDue, todayIsoDate,
} from '../../src/index';
import { useAdmin } from '../session';
import { useConsole } from '../console';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { usePlaceNames } from '../i18n/scope-name';
import { DetailRow as Row } from '../components/detail-row';
import { DrawerStack } from '../components/drawer-stack';

/**
 * Finance › Partner billing (W3-PARTNER-BILLING, D368, ADR-0048) — the platform side of the partner account's bill:
 *
 *   - every partner account with its open exposure (limit, committed = in progress + completed-unpaid, available) and
 *     the payments waiting for approval;
 *   - an account's monthly statements (a calendar month in the account's billing timezone), and closing the open period
 *     early through the design-system dialog with a reason code (the idempotency key is minted once per click);
 *   - a statement's lines, totals and payments in the record drawer, where a FINANCE maker records the payment of a
 *     CLOSED statement (its full total, the paid-on date, the bank reference);
 *   - the approval queue, where a DIFFERENT operator approves or rejects. The console hides the buttons from the maker,
 *     but the authority is the server, which compares the SESSION identities (`403 SELF_APPROVAL`).
 *
 * W6-REFUND-FOUR-EYES (D398, ADR-0063): a credit to a partner is a CREDIT NOTE on a CLOSED statement. The drawer shows
 * the credits issued (and those waiting for a second person) and what the partner is asked to pay — the AMOUNT DUE, total
 * less its credits, which is what the payment is recorded for. An operator holding `partner_billing:credit` proposes a
 * credit (amount through the design system's money input in the statement currency, the market when the statement covers
 * several, a reason from the vocabulary); the server routes it against the governed refund threshold — issued at once
 * within it, otherwise waiting in Finance › Aprovações de reembolso for a DIFFERENT operator.
 *
 * Money renders from integer minor units with the currency's exponent; every number is tabular; one primary action per
 * view (the drawer's "record payment"; the credit is a secondary action).
 */
function newKey(): string {
  const c = globalThis.crypto;
  if (typeof c?.randomUUID === 'function') return c.randomUUID();
  const bytes = new Uint8Array(16);
  c.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

export function PartnerBillingScreen(): ReactElement {
  const { runtime, me, grants } = useAdmin();
  const selfApproval = maySelfApprove(grants); // D868: a GLOBAL super administrator decides their own proposals
  const { mayCredit } = refundAuthority(grants);
  const { confirm, toast } = useConsole();
  const { t, lang } = useT();
  const { v, fail, explain } = useVocab();
  const place = usePlaceNames();
  const locale = lang;
  const operatorId = me?.id;
  const [accounts, setAccounts] = useState<readonly PartnerBillingAccount[] | undefined>(undefined);
  const [pending, setPending] = useState<readonly PartnerStatementPayment[]>([]);
  const [selected, setSelected] = useState<PartnerBillingAccount | undefined>(undefined);
  const [statements, setStatements] = useState<readonly PartnerStatement[] | undefined>(undefined);
  const [detail, setDetail] = useState<PartnerStatementDetail | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const [amount, setAmount] = useState('');
  const [paidOn, setPaidOn] = useState(todayIsoDate(Date.now()));
  const [reference, setReference] = useState('');
  const [creditAmount, setCreditAmount] = useState<bigint | undefined>(undefined);
  const [creditMarket, setCreditMarket] = useState('');
  const [creditReason, setCreditReason] = useState(CREDIT_NOTE_REASON_CODES[0] ?? '');
  const [creditNote, setCreditNote] = useState('');
  const [creditMsg, setCreditMsg] = useState<{ tone: 'success' | 'danger'; text: string } | undefined>(undefined);

  const refusal = useCallback((e: ApiError): string => explain(e) ?? t('pbill.refusal.generic', { code: fail(e) }), [t, fail, explain]);

  const loadAccounts = useCallback(async (): Promise<void> => {
    const [acc, queue] = await Promise.all([runtime.partnerBilling.accounts(), runtime.partnerBilling.payments('PENDING_APPROVAL')]);
    if (acc.ok) setAccounts(acc.value.accounts);
    else {
      setAccounts([]);
      setError(refusal(acc.error));
    }
    if (queue.ok) setPending(queue.value.payments);
  }, [runtime, refusal]);

  const loadStatements = useCallback(async (account: PartnerBillingAccount): Promise<void> => {
    setSelected(account);
    setStatements(undefined);
    const res = await runtime.partnerBilling.statements(account.accountId);
    if (res.ok) setStatements(res.value.statements);
    else {
      setStatements([]);
      setError(refusal(res.error));
    }
  }, [runtime, refusal]);

  const openStatement = useCallback(async (statementId: string): Promise<void> => {
    const res = await runtime.partnerBilling.statement(statementId);
    if (res.ok) {
      setDetail(res.value);
      // D398: the payment is the AMOUNT DUE (the total less its credit notes).
      setAmount(statementAmountDue(res.value.statement)?.amountMinor ?? '');
      setReference('');
      setCreditAmount(undefined);
      setCreditMarket(res.value.markets?.[0] ?? '');
      setCreditNote('');
    } else setError(refusal(res.error));
  }, [runtime, refusal]);

  useEffect(() => {
    void loadAccounts();
  }, [loadAccounts]);

  const refreshAll = async (): Promise<void> => {
    await loadAccounts();
    if (selected !== undefined) await loadStatements(selected);
    if (detail !== undefined) await openStatement(detail.statement.statementId);
  };

  const closeEarly = async (): Promise<void> => {
    if (selected === undefined || busy) return;
    const labels = EARLY_CLOSE_REASON_CODES.map((c) => v('billing.early_close_reason', c));
    const answer = await confirm({
      title: t('pbill.close.title', { account: selected.displayName ?? selected.legalName }),
      body: t('pbill.close.body'),
      confirmLabel: t('pbill.close.confirm'),
      cancelLabel: t('pbill.cancel'),
      icon: 'pause',
      reasons: labels,
      reasonLabel: t('pbill.close.reasonLabel'),
    });
    if (!answer.confirmed || answer.reason === undefined) return;
    const reasonCode = EARLY_CLOSE_REASON_CODES[labels.indexOf(answer.reason)];
    if (reasonCode === undefined) return;
    setBusy(true);
    const res = await runtime.partnerBilling.closePeriod(selected.accountId, { reasonCode: reasonCode as never, idempotencyKey: newKey() });
    setBusy(false);
    if (!res.ok) {
      setError(refusal(res.error));
      return;
    }
    toast({ message: t('pbill.close.done'), tone: 'success' });
    await refreshAll();
  };

  const record = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (detail === undefined || busy) return;
    setBusy(true);
    const res = await runtime.partnerBilling.recordPayment(detail.statement.statementId, { amountMinor: amount.trim(), currency: detail.statement.currency, paidOn, externalReference: reference.trim() });
    setBusy(false);
    if (!res.ok) {
      setError(refusal(res.error));
      return;
    }
    toast({ message: t('pbill.record.done'), tone: 'success' });
    await refreshAll();
  };

  const issueCredit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (detail === undefined || busy || creditAmount === undefined || creditAmount <= 0n) return;
    setBusy(true);
    setCreditMsg(undefined);
    const markets = detail.markets ?? [];
    const res = await runtime.partnerBilling.issueCreditNote(detail.statement.statementId, {
      amountMinor: creditAmount.toString(),
      currency: detail.statement.currency,
      reasonCode: creditReason as never,
      ...(markets.length > 1 && creditMarket !== '' ? { marketId: creditMarket } : {}),
      ...(creditNote.trim() !== '' ? { note: creditNote.trim() } : {}),
    });
    setBusy(false);
    if (!res.ok) {
      setCreditMsg({ tone: 'danger', text: explain(res.error) ?? t('ui.actionFailed', { code: fail(res.error) }) });
      return;
    }
    const text = t(res.value.approval.status === 'PENDING' ? 'pcredit.queued' : 'pcredit.issued', { amount: formatWireMoney(res.value.approval.amount, locale) });
    await refreshAll();
    setCreditMsg({ tone: 'success', text });
  };

  const decide = async (payment: PartnerStatementPayment, kind: 'approve' | 'reject'): Promise<void> => {
    if (busy) return;
    let reasonCode: string | undefined;
    if (kind === 'reject') {
      const labels = PAYMENT_REJECT_REASON_CODES.map((c) => v('billing.payment_reject_reason', c));
      const answer = await confirm({ title: t('pbill.reject.title'), body: t('pbill.reject.body'), confirmLabel: t('pbill.reject'), cancelLabel: t('pbill.cancel'), tone: 'danger', reasons: labels, reasonLabel: t('pbill.close.reasonLabel') });
      if (!answer.confirmed || answer.reason === undefined) return;
      reasonCode = PAYMENT_REJECT_REASON_CODES[labels.indexOf(answer.reason)];
      if (reasonCode === undefined) return;
    }
    setBusy(true);
    const res = kind === 'approve' ? await runtime.partnerBilling.approvePayment(payment.paymentId) : await runtime.partnerBilling.rejectPayment(payment.paymentId, reasonCode as never);
    setBusy(false);
    if (!res.ok) {
      setError(refusal(res.error));
      return;
    }
    toast({ message: t(kind === 'approve' ? 'pbill.approve.done' : 'pbill.reject.done'), tone: 'success' });
    await refreshAll();
  };

  const money = (m: { amountMinor: string; currency: string } | null | undefined): string => (m === null || m === undefined ? '—' : formatWireMoney(m, locale));
  const range = (s: PartnerStatement): string =>
    t('pbill.range', { from: formatMarketInstant(s.period.start, s.period.timezone, locale, 'date'), to: formatMarketInstant(new Date(Date.parse(s.period.end) - 1).toISOString(), s.period.timezone, locale, 'date') });

  const accountColumns: readonly Column<PartnerBillingAccount>[] = [
    { key: 'account', header: t('pbill.col.account'), render: (a) => a.displayName ?? a.legalName },
    { key: 'limit', header: t('pbill.col.limit'), numeric: true, render: (a) => <span className="tnum">{a.exposure !== null ? money(a.exposure.limit) : t('pbill.uncapped')}</span> },
    {
      key: 'committed',
      header: t('pbill.col.committed'),
      numeric: true,
      render: (a) => {
        const permille = exposurePermille(a.exposure);
        return <span className="tnum">{a.exposure !== null ? `${money(a.exposure.committed)}${permille !== undefined ? ` · ${Math.round(permille / 10)}%` : ''}` : '—'}</span>;
      },
    },
    { key: 'available', header: t('pbill.col.available'), numeric: true, render: (a) => <span className="tnum">{a.exposure !== null ? money(a.exposure.available) : '—'}</span> },
    { key: 'pending', header: t('pbill.col.pending'), numeric: true, render: (a) => (a.pendingPayments > 0 ? <Badge tone="warning">{String(a.pendingPayments)}</Badge> : <span className="tnum">0</span>) },
  ];
  const statementColumns: readonly Column<PartnerStatement>[] = [
    { key: 'period', header: t('pbill.col.period'), render: (s) => <span className="tnum">{range(s)}</span> },
    { key: 'status', header: t('pbill.col.status'), width: '190px', render: (s) => <Badge tone={partnerStatementTone(s.status)}>{v('billing.partner_statement_status', s.status)}</Badge> },
    { key: 'orders', header: t('pbill.col.orders'), numeric: true, render: (s) => <span className="tnum">{s.chargeCount ?? '—'}</span> },
    { key: 'total', header: t('pbill.col.total'), numeric: true, render: (s) => <span className="tnum">{money(s.total)}</span> },
  ];
  const queueColumns: readonly Column<PartnerStatementPayment>[] = [
    { key: 'account', header: t('pbill.col.account'), render: (p) => p.accountName ?? p.accountId ?? '—' },
    { key: 'amount', header: t('pbill.col.amount'), numeric: true, render: (p) => <span className="tnum">{money(p.amount)}</span> },
    { key: 'paidOn', header: t('pbill.col.paidOn'), render: (p) => <span className="tnum">{p.paidOn}</span> },
    { key: 'reference', header: t('pbill.col.reference'), render: (p) => <span className="tnum">{p.externalReference ?? '—'}</span> },
    {
      key: 'act',
      header: '',
      width: 'max-content',
      render: (p) =>
        canDecidePayment(p, operatorId, selfApproval) ? (
          <Cluster gap={2} justify="end">
            <Button variant="secondary" disabled={busy} onClick={() => void decide(p, 'approve')}>{t('pbill.approve')}</Button>
            <Button variant="ghost" disabled={busy} onClick={() => void decide(p, 'reject')}>{t('pbill.reject')}</Button>
          </Cluster>
        ) : (
          <Text role="caption" tone="secondary" block className="vello-ab-end">{t('pbill.queue.yours')}</Text>
        ),
    },
  ];
  const lineColumns: readonly Column<PartnerStatementLine>[] = [
    { key: 'when', header: t('pbill.col.when'), render: (l) => <span className="tnum">{formatMarketInstant(l.settledAt, detail?.statement.period.timezone, locale)}</span> },
    { key: 'service', header: t('pbill.col.service'), render: (l) => v('business.partner_vertical', l.vertical) },
    { key: 'amount', header: t('pbill.col.amount'), numeric: true, render: (l) => <span className="tnum">{money(l.amount)}</span> },
  ];

  const hasOpen = statements?.some((s) => s.status === 'OPEN') === true;
  const d = detail;
  const payments = d?.payments ?? [];
  const creditNotes = d?.creditNotes ?? [];
  const pendingCredits = d?.pendingCreditNotes ?? [];
  const credited = d?.statement.credited;
  const hasCredits = credited !== undefined && /^\d+$/.test(credited.amountMinor) && BigInt(credited.amountMinor) > 0n;

  return (
    <>
      <PageHeader title={t('pbill.title')} subtitle={t('pbill.subtitle')} />
      {error !== undefined && (
        <Notice tone="danger">{error}</Notice>
      )}

      <Panel>
        <Stack gap={4}>
          <SectionTitle>{t('pbill.queue.title')}</SectionTitle>
          <ListTable columns={queueColumns} rows={pending} rowKey={(p) => p.paymentId} empty={<EmptyState title={t('pbill.queue.empty')} />} minWidth={720} />
        </Stack>
      </Panel>

      <Panel>
        <Stack gap={4}>
          <SectionTitle>{t('pbill.accounts.title')}</SectionTitle>
          <ListTable
            columns={accountColumns}
            rows={accounts ?? []}
            rowKey={(a) => a.accountId}
            onRowClick={(a) => void loadStatements(a)}
            {...(selected !== undefined ? { selectedKey: selected.accountId } : {})}
            loading={accounts === undefined}
            loadingLabel={t('ui.loading')}
            empty={<EmptyState title={t('pbill.accounts.empty')} />}
            minWidth={720}
          />
        </Stack>
      </Panel>

      <Panel>
        <Stack gap={4}>
          {selected === undefined ? (
            <Text as="p" tone="secondary">{t('pbill.statements.pick')}</Text>
          ) : (
            <>
              <Cluster gap={4} align="start" justify="between" wrap>
                <div>
                  <SectionTitle>{t('pbill.statements.title', { account: selected.displayName ?? selected.legalName })}</SectionTitle>
                  <Text as="p" role="caption" tone="secondary">{t('pbill.statements.tz', { timezone: selected.billingTimezone ?? 'UTC' })}</Text>
                </div>
                <Button variant="secondary" disabled={busy || !hasOpen} onClick={() => void closeEarly()}>{t('pbill.close.action')}</Button>
              </Cluster>
              <ListTable
                columns={statementColumns}
                rows={statements ?? []}
                rowKey={(s) => s.statementId}
                onRowClick={(s) => void openStatement(s.statementId)}
                loading={statements === undefined}
                loadingLabel={t('ui.loading')}
                empty={<EmptyState title={t('pbill.statements.empty')} />}
                minWidth={640}
              />
            </>
          )}
        </Stack>
      </Panel>

      <Drawer open={d !== undefined} onClose={() => setDetail(undefined)} labelledBy="pbill-detail-title">
        {d !== undefined && (
          <>
            <DrawerHeader titleId="pbill-detail-title" title={t('pbill.detail.title', { period: d.statement.period.label })} actions={<Badge tone={partnerStatementTone(d.statement.status)}>{v('billing.partner_statement_status', d.statement.status)}</Badge>} />
            <DrawerBody>
              <DrawerStack gap="var(--space-5)">
                <div>
                  <Row label={t('pbill.col.period')} value={range(d.statement)} />
                  <Row label={t('pbill.col.total')} value={money(d.statement.total)} />
                  {hasCredits && <Row label={t('pcredit.credited')} value={money(credited)} />}
                  {d.statement.status !== 'OPEN' && <Row label={t('pcredit.amountDue')} value={money(statementAmountDue(d.statement))} />}
                  <Row label={t('pbill.col.orders')} value={String(d.statement.chargeCount ?? d.lines.length)} />
                  {d.byVertical.map((row) => <Row key={row.vertical} label={`${v('business.partner_vertical', row.vertical)} (${row.count})`} value={money(row.total)} />)}
                  {d.statement.closedAt !== null && (
                    <Row
                      label={formatMarketInstant(d.statement.closedAt, d.statement.period.timezone, locale)}
                      value={d.statement.closedByScheduler === true ? t('pbill.detail.closedBy.scheduler') : t('pbill.detail.closedBy.operator', { operator: d.statement.closedBy ?? '—', reason: v('billing.early_close_reason', d.statement.closeReasonCode ?? 'PARTNER_REQUEST') })}
                    />
                  )}
                </div>
                <div>
                  <SectionTitle level={3} size="sub">{t('pbill.detail.lines')}</SectionTitle>
                  <ListTable columns={lineColumns} rows={d.lines} rowKey={(l) => l.orderId} minWidth={400} />
                </div>
                <div>
                  <SectionTitle level={3} size="sub">{t('pbill.detail.payments')}</SectionTitle>
                  {payments.length === 0 && <Text as="p" role="caption" tone="secondary">{t('pbill.detail.noPayments')}</Text>}
                  {payments.map((p) => (
                    <Row key={p.paymentId} label={`${p.paidOn} · ${p.externalReference ?? ''}`} value={<Badge tone={partnerPaymentTone(p.status)}>{v('billing.partner_payment_status', p.status)}</Badge>} />
                  ))}
                </div>
                <div>
                  <SectionTitle level={3} size="sub">{t('pcredit.title')}</SectionTitle>
                  {creditNotes.length === 0 && pendingCredits.length === 0 && <Text as="p" role="caption" tone="secondary">{t('pcredit.none')}</Text>}
                  {creditNotes.map((n) => (
                    <Row
                      key={n.creditNoteId}
                      label={`${formatMarketInstant(n.issuedAt, d.statement.period.timezone, locale)} · ${v('billing.credit_note_reason', n.reasonCode)}`}
                      value={<span>{money(n.amount)} <Badge tone="success">{t(n.approvedBy !== null && n.approvedBy !== undefined ? 'pcredit.byChecker' : 'pcredit.single')}</Badge></span>}
                    />
                  ))}
                  {pendingCredits.map((p) => (
                    <Row key={p.approvalId} label={v('billing.credit_note_reason', p.reasonCode)} value={<span>{money(p.amount)} <Badge tone="warning">{t('pcredit.pending')}</Badge></span>} />
                  ))}
                </div>
                {mayCredit && canIssueCreditNote(d.statement, payments) && (
                  <form onSubmit={(e) => void issueCredit(e)} noValidate>
                    <Stack gap={4}>
                      <SectionTitle level={3} size="sub">{t('pcredit.form.title')}</SectionTitle>
                      <Text as="p" role="caption" tone="secondary">{t('pcredit.form.hint')}</Text>
                      {creditMsg !== undefined && (
                        <Notice tone={creditMsg.tone}>{creditMsg.text}</Notice>
                      )}
                      <MoneyInput id="pcredit-amount" label={t('pcredit.form.amount')} currency={d.statement.currency} locale={locale} valueMinor={creditAmount} onChangeMinor={setCreditAmount} issueText={(issue) => t(`money.issue.${issue}`)} required />
                      {(d.markets ?? []).length > 1 && (
                        <Field label={t('pcredit.form.market')} htmlFor="pcredit-market">
                          <Select id="pcredit-market" value={creditMarket} onChange={(e) => setCreditMarket(e.target.value)}>
                            {(d.markets ?? []).map((m) => <option key={m} value={m}>{place.market(m)}</option>)}
                          </Select>
                        </Field>
                      )}
                      <Field label={t('pcredit.form.reason')} htmlFor="pcredit-reason">
                        <Select id="pcredit-reason" value={creditReason} onChange={(e) => setCreditReason(e.target.value)}>
                          {CREDIT_NOTE_REASON_CODES.map((c) => <option key={c} value={c}>{v('billing.credit_note_reason', c)}</option>)}
                        </Select>
                      </Field>
                      <Field label={t('pcredit.form.note')} htmlFor="pcredit-note">
                        <TextInput id="pcredit-note" maxLength={500} value={creditNote} onChange={(e) => setCreditNote(e.target.value)} />
                      </Field>
                      <Button type="submit" variant="secondary" block disabled={busy || creditAmount === undefined || creditAmount <= 0n}>{t('pcredit.form.submit')}</Button>
                    </Stack>
                  </form>
                )}
                {canRecordPayment(d.statement, payments) && (
                  <form onSubmit={(e) => void record(e)} noValidate>
                    <Stack gap={4}>
                      <SectionTitle level={3} size="sub">{t('pbill.record.title')}</SectionTitle>
                      <Text as="p" role="caption" tone="secondary">{t('pbill.record.hint')}</Text>
                      <Field label={t('pcredit.amountDue')} htmlFor="pbill-amount" hint={money(statementAmountDue(d.statement))}>
                        <TextInput id="pbill-amount" className="tnum" inputMode="numeric" value={amount} onChange={(e) => setAmount(e.target.value)} readOnly />
                      </Field>
                      <Field label={t('pbill.record.paidOn')} htmlFor="pbill-paid-on">
                        <TextInput id="pbill-paid-on" type="date" className="tnum" value={paidOn} onChange={(e) => setPaidOn(e.target.value)} required />
                      </Field>
                      <Field label={t('pbill.record.reference')} htmlFor="pbill-reference">
                        <TextInput id="pbill-reference" className="tnum" maxLength={120} value={reference} onChange={(e) => setReference(e.target.value)} required />
                      </Field>
                      <Button type="submit" variant="primary" block disabled={busy || reference.trim() === '' || paidOn === ''}>{t('pbill.record.submit')}</Button>
                    </Stack>
                  </form>
                )}
              </DrawerStack>
            </DrawerBody>
          </>
        )}
      </Drawer>
    </>
  );
}
